/**
 * Impresión de recetas, certificados, consentimientos, recibos y demás hojas.
 *
 * Antes la hoja se imprimía «escondiendo» el resto de la pantalla con estilos, y según el navegador (Windows, Mac, iPad…) quedaban
 * hojas en blanco. Ahora, justo antes de imprimir, se copia SOLO la hoja a un contenedor propio al final de la página y, mientras se
 * imprime, todo lo demás se quita del diseño (`display:none`): no ocupa espacio y no puede generar hojas vacías. No depende de
 * funciones nuevas de los navegadores. Al terminar (o al tocar de nuevo la pantalla) la copia se borra.
 */
/** Área útil de una hoja (la más chica entre A4 y Carta, con los márgenes del navegador), en píxeles de pantalla. */
const ANCHO_UTIL = 700, ALTO_UTIL = 930, ESCALA_MINIMA = 0.55;
let copia: HTMLElement | null = null;

/** Cuánto hay que achicar la hoja para que entre en una sola página (1 = no hace falta). */
function factorParaUnaHoja(contenedor: HTMLElement, hoja: HTMLElement): number {
  const antes = { c: contenedor.getAttribute("style"), h: hoja.getAttribute("style") };
  contenedor.style.cssText = `display:block;position:absolute;left:-99999px;top:0;width:${ANCHO_UTIL}px;visibility:hidden`;
  hoja.style.cssText = "display:block;position:static;width:100%;padding:24px;box-sizing:border-box;min-height:0;height:auto";
  const alto = hoja.scrollHeight;
  if (antes.c === null) contenedor.removeAttribute("style"); else contenedor.setAttribute("style", antes.c);
  if (antes.h === null) hoja.removeAttribute("style"); else hoja.setAttribute("style", antes.h);
  return alto > ALTO_UTIL ? Math.max(ESCALA_MINIMA, ALTO_UTIL / alto) : 1;
}

let preparadaEn = 0;

/** Copia la hoja que haya en pantalla (`.solo-impresion`) a un contenedor de impresión. Devuelve false si no hay hoja (se imprime la pantalla normal). */
export function prepararImpresion(): boolean {
  // la hoja de la pantalla (no la copia que ya se hizo antes); si la pantalla ya la quitó, se conserva la copia que había
  const hoja = document.querySelector<HTMLElement>(".visor .solo-impresion") ?? [...document.querySelectorAll<HTMLElement>(".solo-impresion")].find((e) => !e.closest(".hoja-impresion")) ?? null;
  if (!hoja) return !!copia;
  limpiarImpresion();
  copia = document.createElement("div");
  copia.className = "hoja-impresion";
  const interior = hoja.cloneNode(true) as HTMLElement;
  copia.appendChild(interior);
  document.body.appendChild(copia);
  // todo documento debe entrar en una sola hoja: si es más largo, se reduce proporcionalmente (zoom; en navegadores sin zoom, con transform)
  // el visor de PDF firmados trae varias páginas ya armadas: esas no se reducen a una sola hoja
  const f = hoja.hasAttribute("data-sin-ajuste") ? 1 : factorParaUnaHoja(copia, interior);
  if (f < 1) {
    if (typeof CSS !== "undefined" && CSS.supports?.("zoom", "1")) interior.style.setProperty("zoom", String(f));
    else { interior.style.transformOrigin = "top left"; interior.style.transform = `scale(${f})`; interior.style.width = `${100 / f}%`; copia.style.maxHeight = `${ALTO_UTIL}px`; copia.style.overflow = "hidden"; }
  }
  document.body.classList.add("imprimiendo");
  fijarPagina(hoja.getAttribute("data-pagina") === "A5" ? "A5" : "A4", hoja.hasAttribute("data-sin-ajuste"));
  preparadaEn = Date.now();
  return true;
}

/** Tamaño de la hoja de papel: todo sale en A4 vertical y la receta puede salir en A5 (el PDF ya trae su tamaño: sin márgenes del navegador). */
function fijarPagina(tam: "A4" | "A5", pdf: boolean) {
  let e = document.getElementById("pagina-impresion");
  if (!e) { e = document.createElement("style"); e.id = "pagina-impresion"; document.head.appendChild(e); }
  e.textContent = `@page{size:${tam} portrait;margin:${pdf ? "0" : tam === "A5" ? "8mm" : "14mm 12mm"}}`;
}

export function limpiarImpresion() {
  copia?.remove();
  copia = null;
  document.getElementById("pagina-impresion")?.remove();
  document.body.classList.remove("imprimiendo");
}

/** Abre el diálogo de impresión de la hoja que está en pantalla. */
export function imprimir() {
  prepararImpresion();
  // se deja que el navegador (sobre todo Safari en Mac e iPad) termine de armar la hoja antes de abrir la impresión
  requestAnimationFrame(() => setTimeout(() => window.print(), 120));
}

if (typeof window !== "undefined") {
  // Ctrl+P / menú del navegador: también se prepara la hoja (si no se había preparado ya)
  // Ctrl+P: si hay una hoja en pantalla se vuelve a copiar; si la pantalla ya la quitó (pasa justo después de «Imprimir») se usa la copia ya preparada.
  window.addEventListener("beforeprint", () => { prepararImpresion(); });
  // Safari (Mac e iPad) avisa «afterprint» apenas abre el diálogo, antes de armar la vista previa: ahí la hoja se deja hasta que se vuelva a tocar la pantalla
  const esApple = /Mac|iPhone|iPad|iPod/.test(navigator.platform) || (navigator.maxTouchPoints > 1 && /Mac/.test(navigator.userAgent));
  window.addEventListener("afterprint", () => { if (!esApple) limpiarImpresion(); });
  // en iPad y algunos móviles «afterprint» no siempre llega: la copia se quita al volver a tocar la pantalla (pasados unos segundos)
  window.addEventListener("pointerdown", () => { if (copia && Date.now() - preparadaEn > 4000) limpiarImpresion(); }, true);
}
